import type { LandingContent } from '../content';
import { LandingCtaButton } from '../components/LandingCtaButton';
import { Reveal } from '../components/Reveal';
import { SectionContainer } from '../components/SectionContainer';
import type { LandingSectionProps } from '../types';

type BottomCtaSectionProps = LandingSectionProps & {
  content: LandingContent['bottomCta'];
};

export function BottomCtaSection({ t, content, tracking }: BottomCtaSectionProps) {
  return (
    <section className="relative overflow-hidden bg-[#070D17] py-20 text-white sm:py-24 lg:py-[104px]">
      <div
        className="absolute inset-0"
        aria-hidden="true"
        style={{
          backgroundImage:
            'radial-gradient(circle at 16% 24%, rgba(58,118,255,0.28), transparent 20%), radial-gradient(circle at 84% 70%, rgba(168,84,255,0.20), transparent 18%), linear-gradient(180deg, #060B14 0%, #091121 100%)',
        }}
      />

      <SectionContainer className="relative z-10 max-w-[1280px]">
        <Reveal>
          <div className="relative mx-auto max-w-[1120px] lg:min-h-[560px]">
            <div className="relative ml-auto w-full max-w-[900px] overflow-hidden rounded-[24px] border border-white/12 bg-[linear-gradient(180deg,#101726_0%,#0A0E18_100%)] p-3 shadow-[0_28px_70px_rgba(6,8,14,0.28)] sm:p-4 lg:w-[900px]">
              <div className="relative overflow-hidden rounded-[20px] border border-white/10 bg-[#0A0F1A]">
                <picture>
                  <source media="(max-width: 520px)" srcSet="/landing/bottom-cta-mobile.png" />
                  <source media="(max-width: 960px)" srcSet="/landing/bottom-cta-tablet.png" />
                  <img
                    src="/landing/bottom-cta-desktop.png"
                    alt={content.title}
                    className="block h-auto w-full"
                    loading="lazy"
                  />
                </picture>

                <img
                  src="/landing/bottom-cta-message-dark.png"
                  alt=""
                  className="landing-bubble-in absolute left-[5%] top-[52%] z-10 hidden w-[42%] max-w-[372px] drop-shadow-[0_20px_34px_rgba(6,8,14,0.36)] md:block"
                  loading="lazy"
                />
                <img
                  src="/landing/bottom-cta-message-light.png"
                  alt=""
                  className="landing-bubble-in absolute left-[24%] top-[66%] z-10 hidden w-[58%] max-w-[494px] drop-shadow-[0_20px_34px_rgba(6,8,14,0.30)] md:block"
                  style={{ animationDelay: '180ms' }}
                  loading="lazy"
                />
              </div>
            </div>

            <div className="relative z-20 mt-4 max-w-[280px] rounded-[18px] border border-white/18 bg-[linear-gradient(180deg,rgba(24,31,55,0.9),rgba(13,18,33,0.96))] px-6 py-6 shadow-[0_24px_60px_rgba(6,8,14,0.28)] backdrop-blur-md lg:absolute lg:bottom-[56px] lg:left-[18px] lg:mt-0">
              <h2 className="max-w-[182px] font-pangaia text-[48px] font-bold leading-[0.88] tracking-[-0.05em]">
                {content.title}
              </h2>
              <p className="mt-3 max-w-[190px] font-pangram text-[14px] leading-[20px] text-white/72">
                {content.description}
              </p>
              <LandingCtaButton
                label={t.getStarted || 'Get started'}
                placement="bottom_cta"
                tracking={tracking}
                size="sm"
                className="mt-5"
              />
            </div>

            <div className="mt-4 space-y-3 md:hidden">
              <img
                src="/landing/bottom-cta-message-dark.png"
                alt=""
                className="landing-bubble-in ml-0 w-[72%] max-w-[290px]"
                loading="lazy"
              />
              <img
                src="/landing/bottom-cta-message-light.png"
                alt=""
                className="landing-bubble-in ml-auto w-[90%]"
                style={{ animationDelay: '180ms' }}
                loading="lazy"
              />
            </div>
          </div>
        </Reveal>
      </SectionContainer>
    </section>
  );
}
